import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import type { BeforeMount } from "@monaco-editor/react";
import {
  AlertIcon,
  CheckIcon,
  CopyIcon,
  DownloadIcon,
  FileIcon,
  FolderDownIcon,
  FolderIcon,
  SearchIcon,
  UploadIcon,
} from "../ds/icons";
import { TOAST_SURFACE } from "../ds/toast";
import {
  ApiV2Error,
  listSourceFolder,
  searchSourceFiles,
  sourceFileContent,
  SourceFileContent,
  SourceFolder,
  SourceSearch,
} from "../../utils/apiV2";

/**
 * The app's uploaded code in a read-only editor — the tree on the left,
 * Monaco on the right, on its own dark grey as the Agent's code panel is.
 * Fills whatever box it is given: the Source Code screen gives it the rest of
 * the window.
 *
 * File contents come from the API, one file at a time, never from a storage
 * link: access is checked per file, and binary or large files come back as a
 * notice instead of bytes.
 *
 * The tree comes the same way, one folder at a time as it is opened, and
 * search runs on the API. It used to fetch every path of the code up front and
 * build the whole tree in the browser, which froze the page on a project of
 * tens of thousands of files.
 */

// Only loaded when someone opens the viewer.
const Editor = lazy(() => import("@monaco-editor/react"));

// The editor's own dark theme and the Courrier face, as the Agent's code
// panel has them: code on a neutral dark grey reads as code, where the navy
// of the rest of the screen made it read as one more panel of the page.
const EDITOR_BACKGROUND = "#1E1E1E";
const EDITOR_FONT = '"Courrier", "Courier Prime", ui-monospace, monospace';

// Nothing here can be edited, so the JavaScript and TypeScript checkers have
// nothing to say that helps: their errors and hints ("convert to import",
// dotted under require()) are noise over code someone uploaded to run.
type LanguageDefaults = {
  setDiagnosticsOptions: (options: Record<string, boolean>) => void;
};
const quietDiagnostics: BeforeMount = (monaco) => {
  // The CDN build the loader fetches fills `languages.typescript`; 0.55's ESM
  // build moved it to a top-level `typescript` and left a stub (the Agent's
  // code panel reads it the same way).
  const typescript = ((monaco as unknown as { typescript?: unknown })
    .typescript ?? monaco.languages.typescript) as {
    typescriptDefaults?: LanguageDefaults;
    javascriptDefaults?: LanguageDefaults;
  };
  const off = {
    noSemanticValidation: true,
    noSyntaxValidation: true,
    noSuggestionDiagnostics: true,
  };
  typescript.typescriptDefaults?.setDiagnosticsOptions(off);
  typescript.javascriptDefaults?.setDiagnosticsOptions(off);
};

interface SourceFile {
  path: string;
  size: number;
}

type Loadable<T> =
  | { status: "loading" }
  | { status: "error"; message: string }
  | ({ status: "ready" } & T);

// Typing pauses this long before the API is asked.
const SEARCH_DEBOUNCE_MS = 250;

const errorText = (e: unknown, fallback: string) =>
  e instanceof ApiV2Error ? e.message : fallback;

const LANGUAGES: Record<string, string> = {
  js: "javascript",
  mjs: "javascript",
  cjs: "javascript",
  jsx: "javascript",
  ts: "typescript",
  tsx: "typescript",
  mts: "typescript",
  json: "json",
  html: "html",
  htm: "html",
  css: "css",
  scss: "scss",
  less: "less",
  md: "markdown",
  markdown: "markdown",
  xml: "xml",
  svg: "xml",
  yaml: "yaml",
  yml: "yaml",
  toml: "ini",
  ini: "ini",
  env: "ini",
  sh: "shell",
  bash: "shell",
  sql: "sql",
  graphql: "graphql",
  py: "python",
  go: "go",
  rb: "ruby",
  java: "java",
  kt: "kotlin",
  php: "php",
  rs: "rust",
  cs: "csharp",
  swift: "swift",
  scala: "scala",
  ex: "elixir",
  exs: "elixir",
};

const fileName = (path: string) => path.slice(path.lastIndexOf("/") + 1);

const extensionOf = (path: string) => {
  const name = fileName(path);
  return name.includes(".") ? name.split(".").pop()!.toLowerCase() : "";
};

const isDockerfile = (path: string) => {
  const name = fileName(path);
  return name === "Dockerfile" || name.endsWith(".dockerfile");
};

const languageFor = (path: string) => {
  if (isDockerfile(path)) return "dockerfile";
  if (fileName(path) === "Makefile") return "makefile";
  return LANGUAGES[extensionOf(path)] || "plaintext";
};

// Where the viewer opens: the file at the top that says what the app is.
const INITIAL_FILES = [
  "Dockerfile",
  "package.json",
  "requirements.txt",
  "go.mod",
  "README.md",
];

const dirOf = (path: string) =>
  path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";

// A line in the tree that is not a row: loading, an error, "and more".
const TreeHint = ({
  depth,
  isError = false,
  children,
}: {
  depth: number;
  isError?: boolean;
  children: React.ReactNode;
}) => (
  <div
    className={`py-1.5 pr-2 font-inter text-ds-125 ${
      isError ? "text-state-err" : "text-navy-300"
    }`}
    style={{ paddingLeft: 8 + depth * 18 }}
  >
    {children}
  </div>
);

const rowClass = (isSelected: boolean, isFile: boolean) =>
  `b4-focus w-full flex items-center gap-2 pr-2 h-8 rounded-control text-left font-inter text-ds-135 ${
    isSelected
      ? "bg-navy-600 text-white"
      : isFile
      ? "text-navy-100 hover:bg-navy-700"
      : "text-navy-200 hover:bg-navy-700"
  }`;

// Folders carry a disclosure triangle and a trailing slash; a file sits
// where a folder's name starts, so each level reads as one column. A folder's
// rows are drawn from what the API answered for it, and only once it is open.
const FolderRows = ({
  dir,
  depth,
  folders,
  open,
  selected,
  onToggle,
  onSelect,
}: {
  dir: string;
  depth: number;
  folders: Record<string, Loadable<SourceFolder>>;
  open: Set<string>;
  selected: string | null;
  onToggle: (path: string) => void;
  onSelect: (file: SourceFile) => void;
}) => {
  const folder = folders[dir];
  if (!folder || folder.status === "loading") {
    return <TreeHint depth={depth}>Loading…</TreeHint>;
  }
  if (folder.status === "error") {
    return (
      <TreeHint depth={depth} isError>
        {folder.message}
      </TreeHint>
    );
  }
  // The disclosure column only where this level has a folder to open: a
  // plain list of files starts at the icon.
  const hasFolders = folder.entries.some((entry) => entry.type === "FOLDER");
  return (
    <ul className="m-0 p-0 list-none flex flex-col gap-0.5">
      {folder.entries.map((entry) => {
        const isFile = entry.type === "FILE";
        const isOpen = !isFile && open.has(entry.path);
        const isSelected = entry.path === selected;
        return (
          <li key={`${isFile ? "f" : "d"}:${entry.path}`}>
            <button
              type="button"
              aria-expanded={isFile ? undefined : isOpen}
              onClick={() =>
                entry.type === "FILE"
                  ? onSelect({ path: entry.path, size: entry.size })
                  : onToggle(entry.path)
              }
              title={
                entry.type === "FOLDER"
                  ? `${entry.path}/ · ${entry.fileCount.toLocaleString()} ${
                      entry.fileCount === 1 ? "file" : "files"
                    }`
                  : entry.path
              }
              className={rowClass(isSelected, isFile)}
              style={{ paddingLeft: 8 + depth * 18 }}
            >
              {hasFolders ? (
                <span
                  className="w-3 flex-none text-[10px] text-navy-300"
                  aria-hidden
                >
                  {isFile ? "" : isOpen ? "\u25BE" : "\u25B8"}
                </span>
              ) : null}
              <span
                className={`flex-none ${
                  isSelected ? "text-white" : "text-navy-300"
                }`}
              >
                {isFile ? <FileIcon size={14} /> : <FolderIcon size={14} />}
              </span>
              <span className="truncate">
                {isFile ? entry.name : `${entry.name}/`}
              </span>
            </button>
            {isOpen ? (
              <FolderRows
                dir={entry.path}
                depth={depth + 1}
                folders={folders}
                open={open}
                selected={selected}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            ) : null}
          </li>
        );
      })}
      {folder.truncated ? (
        <li>
          <TreeHint depth={depth}>
            {`${folder.entries.length.toLocaleString()} of ${folder.total.toLocaleString()} shown. Search to find the rest.`}
          </TreeHint>
        </li>
      ) : null}
    </ul>
  );
};

// While searching: the matching files as one flat list, the folder dimmed
// after the name.
const SearchRows = ({
  search,
  selected,
  onSelect,
}: {
  search: Loadable<SourceSearch>;
  selected: string | null;
  onSelect: (file: SourceFile) => void;
}) => {
  if (search.status === "loading") {
    return <TreeHint depth={0}>Searching…</TreeHint>;
  }
  if (search.status === "error") {
    return (
      <TreeHint depth={0} isError>
        {search.message}
      </TreeHint>
    );
  }
  if (!search.files.length) {
    return <TreeHint depth={0}>No file matches.</TreeHint>;
  }
  return (
    <ul className="m-0 p-0 list-none flex flex-col gap-0.5">
      {search.files.map((file) => {
        const isSelected = file.path === selected;
        const dir = dirOf(file.path);
        return (
          <li key={file.path}>
            <button
              type="button"
              onClick={() => onSelect(file)}
              title={file.path}
              className={rowClass(isSelected, true)}
              style={{ paddingLeft: 8 }}
            >
              <span
                className={`flex-none ${
                  isSelected ? "text-white" : "text-navy-300"
                }`}
              >
                <FileIcon size={14} />
              </span>
              <span className="flex-none max-w-[60%] truncate">
                {file.path.slice(dir ? dir.length + 1 : 0)}
              </span>
              {dir ? (
                <span className="min-w-0 truncate text-ds-105 text-navy-300">
                  {dir}/
                </span>
              ) : null}
            </button>
          </li>
        );
      })}
      {search.truncated ? (
        <li>
          <TreeHint depth={0}>
            {`First ${search.files.length.toLocaleString()} of ${search.total.toLocaleString()} matches. Type more to narrow it.`}
          </TreeHint>
        </li>
      ) : null}
    </ul>
  );
};

const Notice = ({ children }: { children: React.ReactNode }) => (
  <div className="h-full flex items-center justify-center px-6 text-center text-ds-125 text-navy-200">
    {children}
  </div>
);

// The file bar's buttons, as the Agents code panel draws its own: 28px,
// an icon and no label for the per-file actions, a short label only for the
// whole project's export. What they do is in the tooltip.
const BAR_BUTTON =
  "b4-focus inline-flex flex-none items-center justify-center h-7 rounded-control border border-navy-400 bg-navy-600 text-white transition-[filter] duration-[120ms] enabled:hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed";

const BarIconButton = ({
  label,
  onClick,
  isDisabled,
  children,
}: {
  label: string;
  onClick: () => void;
  isDisabled?: boolean;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    title={label}
    disabled={isDisabled}
    onClick={onClick}
    className={`${BAR_BUTTON} w-7`}
  >
    {children}
  </button>
);

const ExportButton = ({
  onClick,
  isBusy,
}: {
  onClick: () => void;
  isBusy: boolean;
}) => (
  <button
    type="button"
    title="Export the whole code as a .zip"
    disabled={isBusy}
    onClick={onClick}
    className={`${BAR_BUTTON} gap-1.5 px-3 font-inter text-ds-115 font-medium whitespace-nowrap`}
  >
    <FolderDownIcon size={13} />
    {isBusy ? "Preparing…" : "Export"}
  </button>
);

// The screen's own action, on the bar with the rest so the bar reads as one
// row of what you can do with the code — it stood alone on the head, a
// full-size blue button over a strip of small grey ones.
const UploadButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    title="Upload a folder and deploy it"
    onClick={onClick}
    className="b4-focus inline-flex flex-none items-center justify-center gap-1.5 h-7 px-3 rounded-control border border-transparent bg-action text-white font-inter text-ds-115 font-medium whitespace-nowrap shadow-[inset_0_1px_0_rgba(255,255,255,.15)] transition-[filter] duration-[120ms] hover:brightness-110"
  >
    <UploadIcon size={13} />
    Upload
  </button>
);

/** Saves a text file through the browser, named as the file is. */
const saveText = (path: string, text: string) => {
  const url = URL.createObjectURL(
    new Blob([text], { type: "text/plain;charset=utf-8" })
  );
  const link = document.createElement("a");
  link.href = url;
  link.download = path.split("/").pop() || "file.txt";
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const COPIED_MS = 1800;

const CodeViewer = ({
  appId,
  deploymentId,
  reloadKey,
  toolbarStart,
  onExport,
  isExporting = false,
  onUpload,
}: {
  appId: string;
  /** Phone: what goes before the file picker in the toolbar (the version). */
  toolbarStart?: React.ReactNode;
  /** The whole code as a .zip: the file bar's "Export". */
  onExport?: () => void;
  isExporting?: boolean;
  /** New code, on the desktop's file bar. The phone's head has its own. */
  onUpload?: () => void;
  /** One deployment's code; without it, the app's latest. */
  deploymentId?: string;
  /** Changes when the app has new code, so the files are listed again. */
  reloadKey?: string;
}) => {
  const [folders, setFolders] = useState<
    Record<string, Loadable<SourceFolder>>
  >({});
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState<Loadable<SourceSearch> | null>(null);
  // Phone: the tree is a picker over the editor, open only while choosing.
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [openFolders, setOpenFolders] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<SourceFile | null>(null);
  const [content, setContent] = useState<SourceFileContent | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  // Bumped when the code changes, so an answer about the old code is dropped.
  const generation = useRef(0);

  const select = (file: SourceFile) => {
    setSelected(file);
    setIsPickerOpen(false);
  };

  const loadFolder = useCallback(
    (dir: string) => {
      const asked = generation.current;
      setFolders((prev) => ({ ...prev, [dir]: { status: "loading" } }));
      return listSourceFolder(appId, dir, deploymentId)
        .then((folder) => {
          if (asked !== generation.current) return null;
          setFolders((prev) => ({
            ...prev,
            [dir]: { status: "ready", ...folder },
          }));
          return folder;
        })
        .catch((e) => {
          if (asked !== generation.current) return null;
          setFolders((prev) => ({
            ...prev,
            [dir]: {
              status: "error",
              message: errorText(e, "This folder could not be loaded."),
            },
          }));
          return null;
        });
    },
    [appId, deploymentId]
  );

  // The top folder, and the file the viewer opens on.
  useEffect(() => {
    generation.current += 1;
    setFolders({});
    setOpenFolders(new Set());
    setSelected(null);
    setContent(null);
    setQuery("");
    setSearch(null);
    loadFolder("").then((root) => {
      if (!root) return;
      const files = root.entries.filter((entry) => entry.type === "FILE");
      const first =
        INITIAL_FILES.map((name) =>
          files.find((entry) => entry.name === name)
        ).find(Boolean) || files[0];
      if (first && first.type === "FILE") {
        setSelected({ path: first.path, size: first.size });
      }
    });
  }, [loadFolder, reloadKey]);

  // Search runs on the API once typing pauses.
  const trimmed = query.trim();
  useEffect(() => {
    if (!trimmed) {
      setSearch(null);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setSearch({ status: "loading" });
      searchSourceFiles(appId, trimmed, deploymentId)
        .then(
          (result) => !cancelled && setSearch({ status: "ready", ...result })
        )
        .catch(
          (e) =>
            !cancelled &&
            setSearch({
              status: "error",
              message: errorText(e, "The search did not go through."),
            })
        );
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [appId, deploymentId, trimmed, reloadKey]);

  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    setContent(null);
    setContentError(null);
    sourceFileContent(appId, selected.path, deploymentId)
      .then((result) => !cancelled && setContent(result))
      .catch(
        (e) =>
          !cancelled &&
          setContentError(errorText(e, "This file could not be opened."))
      );
    return () => {
      cancelled = true;
    };
  }, [appId, selected, deploymentId]);

  // Opening a folder asks for it the first time, and again after a failure.
  const toggle = (path: string) => {
    const isOpen = openFolders.has(path);
    setOpenFolders((prev) => {
      const next = new Set(prev);
      isOpen ? next.delete(path) : next.add(path);
      return next;
    });
    const folder = folders[path];
    if (!isOpen && (!folder || folder.status === "error")) {
      loadFolder(path);
    }
  };

  const text = content && content.kind === "TEXT" ? content.content : null;

  // The copy button turns into a check for a moment: the feedback is on the
  // button, as in the Agents code panel, not a toast.
  const [isCopied, setIsCopied] = useState(false);
  useEffect(() => {
    if (!isCopied) return;
    const timer = window.setTimeout(() => setIsCopied(false), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [isCopied]);
  useEffect(() => setIsCopied(false), [selected]);

  const copy = async () => {
    if (text === null) return;
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
    } catch (e) {
      toast.error("Could not copy to the clipboard.", {
        className: TOAST_SURFACE,
      });
    }
  };

  const downloadFile = () => {
    if (text === null || !selected) return;
    saveText(selected.path, text);
  };

  // What the file bar holds on the right: the open file's copy and download,
  // then the export of the whole code.
  const fileActions = (
    <span className="flex flex-none items-center gap-1.5">
      {text !== null && selected ? (
        <>
          <BarIconButton
            label={isCopied ? "Copied" : "Copy file contents"}
            onClick={copy}
          >
            {isCopied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
          </BarIconButton>
          <BarIconButton label="Download this file" onClick={downloadFile}>
            <DownloadIcon size={14} />
          </BarIconButton>
        </>
      ) : null}
      {onExport ? (
        <ExportButton onClick={onExport} isBusy={isExporting} />
      ) : null}
    </span>
  );

  const root = folders[""];
  if (root && root.status === "error") {
    return (
      <div
        role="alert"
        className="flex items-center gap-2 text-ds-125 text-state-err"
      >
        <AlertIcon size={14} /> {root.message}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col gap-3">
      {/* Phone: the version and the file as two pickers above the code. */}
      <div className="hidden [@media(max-width:767.98px)]:flex flex-none items-stretch gap-2.5">
        {toolbarStart}
        <button
          type="button"
          aria-expanded={isPickerOpen}
          onClick={() => setIsPickerOpen((open) => !open)}
          className="b4-focus flex-1 min-w-0 h-11 flex items-center justify-between gap-2 rounded-card border border-navy-400 bg-navy-800 px-3.5 font-courrier text-ds-135 text-white"
        >
          <span className="truncate">{selected?.path || "Pick a file"}</span>
          <span className="flex-none text-[10px] text-navy-300" aria-hidden>
            {isPickerOpen ? "\u25B4" : "\u25BE"}
          </span>
        </button>
      </div>

      {/* Desktop: the tree beside the editor. Phone: the editor alone, and the
        tree over it while picking a file. No frame around the pair: it is the
        whole screen, and a box around it was one more edge between the page
        and the code. The rule on top is what separates it from the head. */}
      <div className="relative flex flex-1 min-h-0 border-t border-navy-500 overflow-hidden">
        <div
          className={`w-72 flex-none flex flex-col bg-navy-800 border-r border-navy-500 [@media(max-width:767.98px)]:absolute [@media(max-width:767.98px)]:inset-0 [@media(max-width:767.98px)]:z-10 [@media(max-width:767.98px)]:w-full [@media(max-width:767.98px)]:border-r-0 ${
            isPickerOpen ? "" : "[@media(max-width:767.98px)]:hidden"
          }`}
        >
          {/* The same height and rule as the editor's file bar beside it, so
            the two read as one line across the box. */}
          <div className="flex-none h-14 px-2.5 flex items-center border-b border-navy-500">
            <div className="w-full flex items-center gap-2 h-9 px-2.5 rounded-control bg-navy-900 border border-navy-500 focus-within:border-navy-300">
              <SearchIcon className="flex-none text-navy-300" />
              <input
                className="flex-1 min-w-0 bg-transparent border-0 outline-none font-inter text-ds-135 text-white placeholder:text-navy-300"
                placeholder="Search files"
                aria-label="Search files"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </div>
          </div>
          <div className="flex-1 min-h-0 overflow-auto px-2.5 pt-3 pb-2.5">
            {search && trimmed ? (
              <SearchRows
                search={search}
                selected={selected?.path || null}
                onSelect={select}
              />
            ) : (
              <FolderRows
                dir=""
                depth={0}
                folders={folders}
                open={openFolders}
                selected={selected?.path || null}
                onToggle={toggle}
                onSelect={select}
              />
            )}
          </div>
        </div>

        <div
          className="flex-1 min-w-0 flex flex-col"
          style={{ background: EDITOR_BACKGROUND }}
        >
          <div className="flex-none flex items-center justify-between gap-3 h-14 px-5 bg-navy-800 border-b border-navy-500 [@media(max-width:767.98px)]:hidden">
            <span className="truncate font-courrier text-ds-125 text-white">
              {selected?.path || ""}
            </span>
            <span className="flex flex-none items-center gap-1.5">
              {fileActions}
              {onUpload ? <UploadButton onClick={onUpload} /> : null}
            </span>
          </div>
          {/* Phone: no file bar — the picker above names the file — so the
            actions float over the code's corner. */}
          <span className="hidden [@media(max-width:767.98px)]:block absolute top-2.5 right-2.5 z-[5]">
            {fileActions}
          </span>
          <div className="flex-1 min-h-0">
            {contentError ? (
              <Notice>{contentError}</Notice>
            ) : !selected ? (
              <Notice>Pick a file on the left.</Notice>
            ) : !content ? (
              <Notice>Loading…</Notice>
            ) : content.kind !== "TEXT" ? (
              <Notice>
                {content.kind === "BINARY"
                  ? "This is a binary file. Use Download .zip to get it."
                  : "This file is larger than 1 MB, too large to show here. Use Download .zip to get it."}
              </Notice>
            ) : (
              <Suspense fallback={<Notice>Loading editor…</Notice>}>
                <Editor
                  height="100%"
                  path={content.path}
                  language={languageFor(content.path)}
                  // Without the file's final newline, so the last numbered
                  // line is the last line of text ("5 lines" shows 5).
                  // Copy keeps the file as it is.
                  value={content.content.replace(/\r?\n$/, "")}
                  theme="vs-dark"
                  beforeMount={quietDiagnostics}
                  options={{
                    readOnly: true,
                    domReadOnly: true,
                    minimap: { enabled: false },
                    fontSize: 13,
                    lineHeight: 20,
                    fontFamily: EDITOR_FONT,
                    lineNumbers: "on",
                    lineNumbersMinChars: 4,
                    glyphMargin: false,
                    scrollBeyondLastLine: false,
                    automaticLayout: true,
                    padding: { top: 14, bottom: 14 },
                    renderLineHighlight: "none",
                    scrollbar: {
                      verticalScrollbarSize: 10,
                      horizontalScrollbarSize: 10,
                      useShadows: false,
                    },
                    overviewRulerBorder: false,
                    overviewRulerLanes: 0,
                    hideCursorInOverviewRuler: true,
                    quickSuggestions: false,
                    contextmenu: false,
                    folding: true,
                    showFoldingControls: "mouseover",
                  }}
                />
              </Suspense>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeViewer;
